/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { UiIcon } from '../common/icons/ui-icon'
import { useOpenNewPageModal } from '../common/page-settings-modal/new-page-modal-context'
import { RecentNotesList } from './recent-notes-list'
import { sortByLastChangedDesc } from './sort-by-last-changed'
import type { NoteTreeEntryInterface } from '@hedgedoc/commons'
import React, { useCallback, useMemo } from 'react'
import { Badge, Button, Card } from 'react-bootstrap'
import { Folder as IconFolder, Plus as IconPlus } from 'react-bootstrap-icons'
import { Trans, useTranslation } from 'react-i18next'

const NOTES_PER_FOLDER = 5

export interface FolderCardProps {
  name: string
  /** The path of the folder, or undefined for the unfiled notes */
  path?: string
  notes: NoteTreeEntryInterface[]
}

/**
 * Renders a card for a top-level folder of the navigation tree with its most recently changed notes.
 */
export const FolderCard: React.FC<FolderCardProps> = ({ name, path, notes }) => {
  useTranslation()
  const recentNotes = useMemo(() => sortByLastChangedDesc(notes).slice(0, NOTES_PER_FOLDER), [notes])
  const openNewPageModal = useOpenNewPageModal()
  const onNewNoteClick = useCallback(() => openNewPageModal({ path }), [openNewPageModal, path])

  return (
    <Card className={'h-100'}>
      <Card.Header className={'d-flex align-items-center gap-2'}>
        <UiIcon icon={IconFolder} />
        <strong className={'flex-fill text-truncate'}>{name}</strong>
        <Badge bg={'secondary'} pill>
          {notes.length}
        </Badge>
        {path !== undefined && (
          <Button size={'sm'} variant={'outline-secondary'} onClick={onNewNoteClick}>
            <UiIcon icon={IconPlus} />
            <Trans i18nKey={'homePage.newNote'} />
          </Button>
        )}
      </Card.Header>
      <RecentNotesList notes={recentNotes} showPath={true} />
    </Card>
  )
}
